import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CustomerAddressDto,
  CustomerApiService,
  CustomerProfileDto,
} from '../../../services/customer-api.service';
import { CustomerAssigneeLookup, LookupApiService } from '../../../services/lookup-api.service';

/**
 * Editable customer profile content shared by the New and Edit modes of
 * CustomerPageComponent. This component does not own a workspace page shell.
 */
@Component({
  selector: 'app-customer-profile-editor',
  templateUrl: './customer-profile-editor.component.html',
  styleUrls: ['./customer-profile-editor.component.css'],
  host: { class: 'workspace-page', '[class.workspace-page--full]': '!inline' },
})
export class CustomerProfileEditorComponent implements OnChanges, OnInit, OnDestroy {
  @Input() mode: 'new' | 'edit' | 'view' = 'new';
  @Input() customerId: number | null = null;
  @Input() inline = false;
  @Output() profileLoaded = new EventEmitter<CustomerProfileDto>();
  @Output() completed = new EventEmitter<number>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  formError = '';
  customerTypesLoading = true;
  sourcesLoading = true;
  assigneesLoading = true;
  statesLoading = true;

  sources: string[] = [];
  customerTypes: { id: number; name: string }[] = [];
  users: { id: number; name: string }[] = [];
  states: { id: number; code: string; label: string }[] = [];

  private hadServiceAddress = false;
  private hadBillingAddress = false;
  private loadedProfile: CustomerProfileDto | null = null;
  private readonly returnToCollection: boolean;
  private readonly destroyed$ = new Subject<void>();
  private initialized = false;
  private profileLoadVersion = 0;

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: CustomerApiService,
    private readonly lookups: LookupApiService,
    route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {
    this.returnToCollection = route.parent?.routeConfig?.path === 'table';
  }

  get isEdit(): boolean { return this.mode === 'edit'; }
  get isView(): boolean { return this.mode === 'view'; }
  get loadsExistingCustomer(): boolean { return this.isEdit || this.isView; }
  get submitLabel(): string { return this.isEdit ? 'Update Customer' : 'Create Customer'; }
  get savingLabel(): string { return this.isEdit ? 'Updating...' : 'Creating...'; }
  get assigneeOptions(): CustomerAssigneeLookup[] {
    return this.users.map(user => ({ id: user.id, displayName: user.name, userName: null }));
  }

  ngOnInit(): void {
    const requiredAddress = this.mode === 'new' ? [Validators.required] : [];
    this.form = this.fb.group({
      customerInfo: this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(128)]],
        customerTypeId: [null as number | null],
        source: [null as string | null],
        assigneeUserId: [null as number | null],
        phone: ['', Validators.maxLength(32)],
        ext: ['', Validators.maxLength(50)],
        mobilePhone: ['', Validators.maxLength(50)],
        email: ['', [Validators.email, Validators.maxLength(128)]],
        website: ['', Validators.maxLength(255)],
        contact: ['', Validators.maxLength(50)],
        fax: ['', Validators.maxLength(50)],
        notes: [''],
      }),
      serviceAddress: this.fb.group({
        line1: ['', [...requiredAddress, Validators.maxLength(100)]],
        line2: ['', Validators.maxLength(100)],
        city: ['', [...requiredAddress, Validators.maxLength(64)]],
        stateId: [null as number | null, requiredAddress],
        zip: ['', [...requiredAddress, Validators.maxLength(12)]],
      }),
      billingAddress: this.fb.group({
        sameAsService: [this.mode === 'new'],
        line1: ['', Validators.maxLength(100)],
        line2: ['', Validators.maxLength(100)],
        city: ['', Validators.maxLength(64)],
        stateId: [null as number | null],
        zip: ['', Validators.maxLength(12)],
      }),
    });

    this.form.get('serviceAddress')!.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(value => {
        if (this.sameAsService) this.copyServiceToBilling(value);
      });
    this.form.get('billingAddress.sameAsService')!.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(same => this.setBillingMode(Boolean(same)));
    this.setBillingMode(this.mode === 'new');

    this.loadLookupOptions();
    this.initialized = true;
    if (this.loadsExistingCustomer) this.loadCustomerProfile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized || !this.loadsExistingCustomer) return;
    if (changes['customerId'] || changes['mode']) {
      if (!this.isView) this.form.enable({ emitEvent: false });
      this.loadCustomerProfile();
    }
  }

  private loadLookupOptions(): void {
    this.lookups.customerTypes()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: customerTypes => {
          this.customerTypes = customerTypes ?? [];
          this.customerTypesLoading = false;
          this.ensureProfileLookupOptions();
        },
        error: () => {
          this.customerTypesLoading = false;
          this.formError ||= 'Could not load customer types.';
        },
      });

    this.lookups.customerSources()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: sources => {
          this.sources = [...(sources ?? [])];
          this.sourcesLoading = false;
          this.ensureProfileLookupOptions();
        },
        error: () => {
          this.sourcesLoading = false;
          this.formError ||= 'Could not load customer sources.';
        },
      });

    this.lookups.customerAssignees()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: assignees => {
          this.users = (assignees ?? []).map(user => {
            const name = user.displayName ?? user.userName ?? `User ${user.id}`;
            return { id: user.id, name: name.includes('@') ? name.split('@')[0] : name };
          });
          this.assigneesLoading = false;
          this.ensureProfileLookupOptions();
        },
        error: () => {
          this.assigneesLoading = false;
          this.formError ||= 'Could not load customer assignees.';
        },
      });

    this.lookups.states()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: states => {
          this.states = states ?? [];
          this.statesLoading = false;
        },
        error: () => {
          this.statesLoading = false;
          this.formError ||= 'Could not load states.';
        },
      });
  }

  private ensureProfileLookupOptions(): void {
    const customer = this.loadedProfile?.customer;
    if (!customer) return;
    if (customer.source && !this.sources.includes(customer.source)) {
      this.sources.push(customer.source);
    }
    if (customer.customerTypeId != null &&
        !this.customerTypes.some(type => type.id === customer.customerTypeId)) {
      this.customerTypes.push({
        id: customer.customerTypeId,
        name: customer.customerTypeName ?? `Type ${customer.customerTypeId}`,
      });
    }
    if (customer.assigneeUserId != null &&
        !this.users.some(user => user.id === customer.assigneeUserId)) {
      this.users.push({
        id: customer.assigneeUserId,
        name: customer.assigneeName ?? `User ${customer.assigneeUserId}`,
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get sameAsService(): boolean {
    return Boolean(this.form.get('billingAddress.sameAsService')!.value);
  }

  save(): void {
    if (this.isView || this.loading || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.formError = '';

    if (this.isEdit) this.updateCustomer();
    else this.createCustomer();
  }

  cancel(): void {
    if (this.inline) { this.cancelled.emit(); return; }
    if (!this.loadsExistingCustomer) {
      this.router.navigate(['/customers']);
      return;
    }
    this.router.navigate(this.returnToCollection
      ? ['/customers/table', this.customerId]
      : ['/customers', this.customerId]);
  }

  private createCustomer(): void {
    const info = this.form.get('customerInfo')!.getRawValue();
    const service = this.addressValue('serviceAddress');
    const billing = this.addressValue('billingAddress');
    this.api.createMasterCustomer({
      displayName: info.name.trim(),
      customerTypeId: info.customerTypeId ?? null,
      source: info.source ?? null,
      assigneeUserId: info.assigneeUserId ?? null,
      phone: this.optionalText(info.phone),
      ext: this.optionalText(info.ext),
      mobilePhone: this.optionalText(info.mobilePhone),
      email: this.optionalText(info.email),
      website: this.optionalText(info.website),
      contact: this.optionalText(info.contact),
      fax: this.optionalText(info.fax),
      notes: this.optionalText(info.notes),
      serviceAddress: service,
      billingSameAsService: this.sameAsService,
      billingAddress: this.sameAsService ? null : billing,
    }).pipe(takeUntil(this.destroyed$)).subscribe({
      next: ({ id }) => this.saved(id, 'Customer created'),
      error: error => this.saveFailed(error, 'Failed to create customer.'),
    });
  }

  private updateCustomer(): void {
    const id = this.customerId!;
    const info = this.form.get('customerInfo')!.getRawValue();
    const service = this.addressValue('serviceAddress');
    const billing = this.sameAsService ? service : this.addressValue('billingAddress');
    const serviceHasValues = this.hasAddressValues(service);
    const billingHasValues = this.hasAddressValues(billing);

    forkJoin([
      this.api.editCustomer(id, {
        displayName: info.name.trim(),
        customerTypeId: info.customerTypeId ?? null,
        source: info.source ?? null,
        assigneeUserId: info.assigneeUserId ?? null,
        phone: this.optionalText(info.phone),
        ext: this.optionalText(info.ext),
        mobilePhone: this.optionalText(info.mobilePhone),
        email: this.optionalText(info.email),
        website: this.optionalText(info.website),
        contact: this.optionalText(info.contact),
        fax: this.optionalText(info.fax),
        notes: this.optionalText(info.notes),
      }),
      this.api.updateCustomerAddresses(id, {
        service: this.hadServiceAddress || serviceHasValues ? service : null,
        billing: this.hadBillingAddress || billingHasValues ? billing : null,
      }),
    ]).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.saved(id, 'Customer updated'),
      error: error => this.saveFailed(error, 'Could not update customer.'),
    });
  }

  private patchProfile(profile: CustomerProfileDto): void {
    this.profileLoaded.emit(profile);
    const customer = profile.customer;
    this.ensureProfileLookupOptions();
    this.form.get('customerInfo')!.patchValue({
      name: customer.displayName,
      customerTypeId: customer.customerTypeId ?? null,
      source: customer.source ?? null,
      assigneeUserId: customer.assigneeUserId ?? null,
      phone: customer.phone ?? '',
      ext: customer.ext ?? '',
      mobilePhone: customer.mobilePhone ?? '',
      email: customer.email ?? '',
      website: customer.website ?? '',
      contact: customer.contact ?? '',
      fax: customer.fax ?? '',
      notes: customer.notes ?? '',
    });
    this.patchAddress('serviceAddress', profile.serviceAddress);
    this.patchAddress('billingAddress', profile.billingAddress);
    this.hadServiceAddress = profile.serviceAddress != null;
    this.hadBillingAddress = profile.billingAddress != null;
    const same = this.addressesEqual(profile.serviceAddress, profile.billingAddress);
    this.form.get('billingAddress.sameAsService')!.setValue(same, { emitEvent: false });
    this.setBillingMode(same);
  }

  private patchAddress(groupName: string, address: CustomerAddressDto | null): void {
    this.form.get(groupName)!.patchValue({
      line1: address?.streetAddressLine1 ?? '',
      line2: address?.streetAddressLine2 ?? '',
      city: address?.city ?? '',
      stateId: address?.stateId ?? null,
      zip: address?.postalCode ?? '',
    }, { emitEvent: false });
  }

  private setBillingMode(same: boolean): void {
    const billing = this.form.get('billingAddress')!;
    const fields = ['line1', 'line2', 'city', 'stateId', 'zip'];
    if (same) {
      this.copyServiceToBilling(this.form.get('serviceAddress')!.getRawValue());
      fields.forEach(field => billing.get(field)!.disable({ emitEvent: false }));
    } else {
      fields.forEach(field => billing.get(field)!.enable({ emitEvent: false }));
    }
  }

  private copyServiceToBilling(value: Record<string, unknown>): void {
    this.form.get('billingAddress')!.patchValue({
      line1: value['line1'],
      line2: value['line2'],
      city: value['city'],
      stateId: value['stateId'],
      zip: value['zip'],
    }, { emitEvent: false });
  }

  private addressValue(groupName: string) {
    const value = this.form.get(groupName)!.getRawValue();
    return {
      streetAddressLine1: this.optionalText(value.line1),
      streetAddressLine2: this.optionalText(value.line2),
      city: this.optionalText(value.city),
      stateId: value.stateId ?? null,
      postalCode: this.optionalText(value.zip),
    };
  }

  private hasAddressValues(address: ReturnType<CustomerProfileEditorComponent['addressValue']>): boolean {
    return Object.values(address).some(value => value !== null);
  }

  private addressesEqual(
    service: CustomerAddressDto | null,
    billing: CustomerAddressDto | null,
  ): boolean {
    if (!service || !billing) return false;
    return service.streetAddressLine1 === billing.streetAddressLine1 &&
      service.streetAddressLine2 === billing.streetAddressLine2 &&
      service.city === billing.city &&
      service.stateId === billing.stateId &&
      service.postalCode === billing.postalCode;
  }

  private optionalText(value: unknown): string | null {
    const text = typeof value === 'string' ? value.trim() : '';
    return text || null;
  }

  private saved(id: number, message: string): void {
    this.saving = false;
    this.snackBar.open(message, undefined, { duration: 2500 });
    if (this.inline) { this.completed.emit(id); return; }
    this.router.navigate(this.returnToCollection
      ? ['/customers/table', id]
      : ['/customers', id]);
  }

  private loadCustomerProfile(): void {
    const version = ++this.profileLoadVersion;
    this.loadedProfile = null;
    this.hadServiceAddress = false;
    this.hadBillingAddress = false;
    this.formError = '';
    this.loading = true;
    if (this.customerId === null) {
      this.loading = false;
      this.formError = 'A customer id is required.';
      return;
    }
    this.api.getCustomerProfile(this.customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: profile => {
          if (version !== this.profileLoadVersion) return;
          this.loadedProfile = profile;
          this.patchProfile(profile);
          if (this.isView) this.form.disable({ emitEvent: false });
          this.loading = false;
        },
        error: () => {
          if (version !== this.profileLoadVersion) return;
          this.loading = false;
          this.formError = 'Could not load this customer.';
        },
      });
  }

  private saveFailed(error: unknown, fallback: string): void {
    this.saving = false;
    const body = error instanceof HttpErrorResponse ? error.error : null;
    this.formError = typeof body?.error === 'string'
      ? body.error
      : typeof body?.message === 'string'
        ? body.message
        : fallback;
  }
}
